import { useEffect, useMemo, useRef, useState } from "react";
import { acquisitionLabel } from "../acquisition";
import {
  VERDICTS, band, formatAverage, lookUpSender, quickRateLists, quickRateRow, quickRateSave, senderRows, senderSummaries,
} from "../card-ratings";
import type { OwnerRating, QuickRateRow, SenderFilter, SenderSort, SenderSummary } from "../card-ratings";
import { formatDate } from "../postcard-dates";
import { RatingStars, StarPicker, TagChips, TagPicker } from "../rating-controls";
import { groupLabel, threadRows } from "../forum-threads";
import type { OwnerThread } from "../forum-threads";

/** The fields of a card this page reads, from the owner's /api/postcards. */
type Card = {
  id: string;
  photoUrl: string;
  senderUser: string;
  originCountry: string;
  acquisitionType: string;
  acquisitionDetail: string;
  receivedDate: string;
  createdAt: string;
  focalX: number;
  focalY: number;
  showWholeCard: boolean;
  ownerRating?: OwnerRating;
  ownerThread?: OwnerThread;
};

type Viewer = "loading" | "owner" | "other";

const BAND_CLASS = { good: "is-good", mixed: "is-mixed", low: "is-low" } as const;

function AveragePill({ average }: { average: number }) {
  return <span className={`average-pill ${BAND_CLASS[band(average)]}`}>{formatAverage(average)}</span>;
}

/** The spread of a sender's ratings as a thin bar, low effort on the left. */
function SpreadBar({ summary }: { summary: SenderSummary }) {
  const words = summary.spread
    .map((count, rating) => count ? `${count} × ${rating === 0 ? "0" : "★".repeat(rating)}` : "")
    .filter(Boolean).join(", ");
  return (
    <span className="spread-bar" role="img" aria-label={words} title={words}>
      {summary.spread.map((count, rating) => count > 0 && (
        <i key={rating} className={`spread-${rating}`} style={{ width: `${(count / summary.rated) * 100}%` }} />
      ))}
    </span>
  );
}

/**
 * The owner's ratings page (#55): the quick-rate list, a sender lookup for before she
 * writes to someone, and every sender in one sortable list.
 *
 * Everything on it comes from `ownerRating`, which the server only puts on cards for the
 * owner. A visitor who finds the URL gets the sign-in prompt and, even if they got past
 * it, a card list with no ratings in it.
 */
export default function RatingsPage() {
  const [viewer, setViewer] = useState<Viewer>("loading");
  const [cards, setCards] = useState<Card[]>([]);
  const [notice, setNotice] = useState("");
  const [which, setWhich] = useState<"recent" | "older">("recent");
  const [pending, setPending] = useState<Record<string, QuickRateRow>>({});
  const [saving, setSaving] = useState<Record<string, boolean>>({});
  const [typed, setTyped] = useState("");
  const [filter, setFilter] = useState<SenderFilter>("all");
  const [minRated, setMinRated] = useState(2);
  const [sort, setSort] = useState<SenderSort>("average");
  const [descending, setDescending] = useState(true);
  const lookupSection = useRef<HTMLElement>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const me = await (await fetch("/api/me", { cache: "no-store" })).json() as { isOwner: boolean };
      if (!me.isOwner) {
        if (!cancelled) setViewer("other");
        return;
      }
      const response = await fetch("/api/postcards", { cache: "no-store" });
      if (!response.ok) throw new Error("Could not load postcards");
      const payload = await response.json() as { postcards: Card[] };
      if (cancelled) return;
      setCards(payload.postcards);
      setViewer("owner");
    })().catch(() => {
      if (!cancelled) {
        setNotice("This page could not load your postcards.");
        setViewer("other");
      }
    });
    return () => { cancelled = true; };
  }, []);

  const lists = useMemo(() => quickRateLists(cards), [cards]);
  const waiting = lists.recent.length + lists.older.length;
  const queue = lists[which];
  const summaries = useMemo(() => senderSummaries(cards), [cards]);
  const lookup = useMemo(() => lookUpSender(summaries, typed), [summaries, typed]);
  const rows = useMemo(
    () => senderRows(summaries, { filter, minRated, sort, descending }),
    [summaries, filter, minRated, sort, descending],
  );

  // A row nobody has touched starts from the card's own tags (quickRateRow), since the
  // save replaces them: starting empty would erase tags added in the form.
  function pendingFor(card: Card) {
    return pending[card.id] ?? quickRateRow(card);
  }

  function updatePending(card: Card, change: Partial<QuickRateRow>) {
    setPending((current) => ({ ...current, [card.id]: { ...(current[card.id] ?? quickRateRow(card)), ...change } }));
  }

  /** Saves a rating and, once the server has it, lets the card drop off the list. */
  async function save(card: Card, rating: OwnerRating) {
    setSaving((current) => ({ ...current, [card.id]: true }));
    try {
      const response = await fetch(`/api/postcards/${card.id}/rating`, {
        method: "PUT",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(rating),
      });
      if (!response.ok) {
        const payload = await response.json().catch(() => ({})) as { error?: string };
        throw new Error(payload.error ?? "Could not save that rating.");
      }
      setCards((current) => current.map((item) => item.id === card.id ? { ...item, ownerRating: rating } : item));
      setPending((current) => {
        const next = { ...current };
        delete next[card.id];
        return next;
      });
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Could not save that rating.");
    } finally {
      setSaving((current) => ({ ...current, [card.id]: false }));
    }
  }

  function pickStars(card: Card, rating: number) {
    const row = pendingFor(card);
    // With the tags open she is still deciding, so the stars wait for Done.
    if (row.tagsOpen) {
      updatePending(card, { rating });
      return;
    }
    void save(card, quickRateSave(row, rating));
  }

  function openInLookup(name: string) {
    setTyped(name);
    lookupSection.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  function sortBy(column: SenderSort) {
    if (column === sort) {
      setDescending((current) => !current);
      return;
    }
    setSort(column);
    // Names read A–Z; the numbers read biggest first.
    setDescending(column !== "name");
  }

  function sortHeader(column: SenderSort, label: string) {
    const active = sort === column;
    return (
      <th aria-sort={active ? (descending ? "descending" : "ascending") : "none"}>
        <button type="button" className={active ? "is-active" : ""} onClick={() => sortBy(column)}>
          {label}{active && (descending ? " ↓" : " ↑")}
        </button>
      </th>
    );
  }

  const header = (
    <header className="topbar">
      <a className="brand" href="/" aria-label="My Collection of Connection home">
        <span className="brand-mark">✉</span>
        <span>My Collection of Connection</span>
      </a>
      <a className="owner-link" href="/">← Back to the collection</a>
    </header>
  );

  if (viewer !== "owner") {
    return (
      <main className="app-shell">
        {header}
        <section className="auth-shell">
          <div className="empty-state auth-card">
            <div className="empty-stamp">{viewer === "loading" ? "···" : "✉"}</div>
            <h1>{viewer === "loading" ? "Opening your ratings…" : "Ratings are private"}</h1>
            {viewer === "other" && <p>{notice || "Only the collection owner can see this page."}</p>}
            {viewer === "other" && !notice && <p className="auth-back"><a href="/signin">Owner sign in</a></p>}
          </div>
        </section>
      </main>
    );
  }

  const profile = lookup.match;
  // "By thread" only once at least one of their cards names a thread: without one, every
  // row would just repeat how the cards came.
  const byThread = profile && profile.cards.some((card) => card.ownerThread?.thread.trim()) ? threadRows(profile.cards) : [];

  return (
    <main className="app-shell ratings-page">
      {header}

      <section className="ratings-intro">
        <p className="eyebrow">Only you can see this page</p>
        <h1>Ratings</h1>
      </section>

      <section className="ratings-section" aria-labelledby="quick-rate-title">
        <div className="section-heading compact">
          <div><p className="eyebrow">Rate when you have time</p><h2 id="quick-rate-title">Quick rate</h2></div>
          <p>{waiting} {waiting === 1 ? "card" : "cards"} waiting</p>
        </div>
        <p className="ratings-hint">Tap the stars and the card moves off the list. To add tags, open them first, then tap Done.</p>
        <div className="segmented" role="group" aria-label="Which cards to show">
          <button type="button" aria-pressed={which === "recent"} onClick={() => setWhich("recent")}>Recently added ({lists.recent.length})</button>
          <button type="button" aria-pressed={which === "older"} onClick={() => setWhich("older")}>Older cards ({lists.older.length})</button>
        </div>
        <div className="quick-rate-list">
          {queue.length === 0 ? (
            <p className="quick-rate-empty">All caught up here.</p>
          ) : queue.map((card) => {
            const row = pendingFor(card);
            const busy = Boolean(saving[card.id]);
            return (
              <article className={`quick-rate-row${busy ? " is-saving" : ""}`} key={card.id} aria-busy={busy}>
                <img
                  className="quick-rate-thumb"
                  src={`${card.photoUrl}?w=320`}
                  alt=""
                  loading="lazy"
                  decoding="async"
                  style={{ objectFit: card.showWholeCard ? "contain" : "cover", objectPosition: `${card.focalX}% ${card.focalY}%` }}
                />
                <div className="quick-rate-top">
                  <div className="quick-rate-meta">
                    <strong>{card.senderUser}</strong>
                    <span>{[card.originCountry, acquisitionLabel(card), `received ${formatDate(card.receivedDate)}`].filter(Boolean).join(" · ")}</span>
                  </div>
                  <StarPicker value={row.rating} onPick={(rating) => pickStars(card, rating)} compact disabled={busy} />
                </div>
                <div className="quick-rate-actions">
                  <button type="button" aria-expanded={row.tagsOpen} onClick={() => updatePending(card, { tagsOpen: !row.tagsOpen })} disabled={busy}>
                    {row.tagsOpen ? "− Hide tags" : "+ Tags"}
                  </button>
                  <button type="button" className="is-quiet" onClick={() => save(card, quickRateSave(row, null))} disabled={busy}>
                    Leave unrated
                  </button>
                </div>
                {row.tagsOpen && (
                  <div className="quick-rate-tags">
                    <TagPicker
                      selected={row.tags}
                      onToggle={(key) => updatePending(card, { tags: row.tags.includes(key) ? row.tags.filter((item) => item !== key) : [...row.tags, key] })}
                    />
                    <div className="quick-rate-done">
                      <button
                        type="button"
                        className="primary-button"
                        disabled={busy || row.rating === null}
                        onClick={() => row.rating !== null && save(card, quickRateSave(row, row.rating))}
                      >Done</button>
                      {row.rating === null && <span>Pick a rating first.</span>}
                    </div>
                  </div>
                )}
              </article>
            );
          })}
        </div>
      </section>

      <section className="ratings-section" aria-labelledby="lookup-title" ref={lookupSection}>
        <div className="section-heading compact">
          <div><p className="eyebrow">Before you send</p><h2 id="lookup-title">Look someone up</h2></div>
        </div>
        <div className="lookup">
          <label className="lookup-search">
            <span>Sender username</span>
            <input type="search" value={typed} onChange={(event) => setTyped(event.target.value)} placeholder="@username" autoComplete="off" spellCheck={false} />
          </label>
          <div className="sender-profile" aria-live="polite">
            {!lookup.key ? (
              <p className="profile-empty">Type a username to see what they've sent you.</p>
            ) : !profile ? (
              lookup.near.length ? (
                <p className="profile-empty">No exact match. Did you mean {lookup.near.map((near, index) => (
                  <span key={near.key}>{index > 0 && ", "}<button type="button" className="link-button" onClick={() => setTyped(near.name)}>{near.name}</button></span>
                ))}?</p>
              ) : (
                <p className="profile-empty"><strong>{typed.trim().replace(/^@+/, "")}</strong> hasn't sent you a card yet.</p>
              )
            ) : (
              <>
                <div className="profile-head">
                  <div><h3>{profile.name}</h3><span>{profile.country}</span></div>
                  {profile.average !== null && <span className={`verdict ${BAND_CLASS[band(profile.average)]}`}>{VERDICTS[band(profile.average)]}</span>}
                </div>
                <div className="profile-facts">
                  <div><small>Average</small><b>{profile.average === null ? "—" : formatAverage(profile.average)}</b></div>
                  <div>
                    <small>Rated cards</small><b>{profile.rated}</b>
                    {profile.unrated > 0 && <span>{profile.unrated} unrated</span>}
                  </div>
                  <div><small>Last card</small><b className="is-date">{formatDate(profile.lastReceived)}</b></div>
                </div>
                <div className="profile-block">
                  <h4>Tags on their cards</h4>
                  {profile.tags.length ? <TagChips tags={profile.tags} /> : <p className="profile-none">None yet</p>}
                </div>
                <div className="profile-block">
                  <h4>Every card from them</h4>
                  {profile.cards.map((card) => (
                    <div className="history-row" key={card.id}>
                      <time>{formatDate(card.receivedDate)}</time>
                      <div>
                        <span>{acquisitionLabel(card) || card.originCountry}</span>
                        {card.ownerRating && card.ownerRating.tags.length > 0 && <TagChips tags={card.ownerRating.tags} />}
                      </div>
                      <RatingStars rating={card.ownerRating?.rating ?? null} sayUnrated />
                    </div>
                  ))}
                </div>
                {byThread.length > 0 && (
                  <div className="profile-block">
                    <h4>By thread</h4>
                    {byThread.map((row) => (
                      <div className={`thread-row${row.isThread ? "" : " is-other"}`} key={row.key}>
                        <span>
                          <b>{row.label}</b>{" "}
                          <span className="profile-none">({row.rated} rated{row.unrated > 0 ? `, ${row.unrated} unrated` : ""})</span>
                          {row.groups.length > 0 && <small>{row.groups.map(groupLabel).join(", ")}</small>}
                        </span>
                        {row.average === null ? <span className="rating-unrated">Unrated</span> : <AveragePill average={row.average} />}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      <section className="ratings-section" aria-labelledby="senders-title">
        <div className="section-heading compact">
          <div><p className="eyebrow">Everyone at once</p><h2 id="senders-title">All your senders</h2></div>
          <p>{rows.length} {rows.length === 1 ? "sender" : "senders"}</p>
        </div>
        <div className="sender-filters">
          <div className="filter-chips" role="group" aria-label="Show">
            {([["all", "Everyone"], ["good", "Usually great"], ["mixed", "Mixed"], ["low", "Usually low effort"]] as const).map(([value, label]) => (
              <button key={value} type="button" className={value === "low" ? "is-low" : ""} aria-pressed={filter === value} onClick={() => setFilter(value)}>{label}</button>
            ))}
          </div>
          <label>
            Based on at least
            <select value={minRated} onChange={(event) => setMinRated(Number(event.target.value))}>
              <option value={1}>1 rated card</option>
              <option value={2}>2 rated cards</option>
              <option value={3}>3 rated cards</option>
            </select>
          </label>
        </div>
        <div className="sender-table-wrap">
          <table className="sender-table">
            <thead>
              <tr>
                {sortHeader("name", "Sender")}
                {sortHeader("average", "Average")}
                {sortHeader("cards", "Cards")}
                {sortHeader("last", "Last card")}
                <th>Tags seen most</th>
              </tr>
            </thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={5} className="table-empty">No senders match. Try “Everyone” or fewer cards.</td></tr>
              ) : rows.map((summary) => (
                <tr key={summary.key}>
                  <td>
                    <button type="button" className="link-button" onClick={() => openInLookup(summary.name)}>{summary.name}</button>
                    <small>{summary.country}</small>
                  </td>
                  <td>{summary.average !== null && <><AveragePill average={summary.average} /><SpreadBar summary={summary} /></>}</td>
                  <td className="is-number">{summary.rated}{summary.unrated > 0 && <small>+{summary.unrated} unrated</small>}</td>
                  <td className="is-number">{formatDate(summary.lastReceived)}</td>
                  <td><TagChips tags={summary.tags.slice(0, 2)} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {notice && <button className="toast" onClick={() => setNotice("")} aria-live="polite">{notice}<span>×</span></button>}
    </main>
  );
}
